import { z } from 'zod';

import type { CustomFieldWithValue } from '@/types/ui';
import type { TFunction } from 'i18next';

export type CustomFieldValidationConfig = {
  fieldId: number;
  isRequired: boolean;
};

export type CustomFieldFormValues = Record<string, string | boolean | null>;

export function createSignUpFormSchema(
  t: TFunction,
  customFieldsConfig: CustomFieldValidationConfig[] = [],
) {
  const requiredFieldIds = new Set(
    customFieldsConfig
      .filter(({ isRequired }) => isRequired)
      .map(({ fieldId }) => String(fieldId)),
  );

  const customFieldsSchema = z
    .record(z.string(), z.union([z.string(), z.boolean(), z.null()]))
    .superRefine((data, ctx) => {
      requiredFieldIds.forEach((fieldId) => {
        const value = data[fieldId];
        if (value === null || value === '' || value === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Поле є обов'язковим"),
            path: [fieldId],
          });
        }
      });
    });

  return z.object({
    first_name: z
      .string()
      .min(1, { message: t("Поле є обов'язковим") })
      .trim(),
    last_name: z
      .string()
      .min(1, { message: t("Поле є обов'язковим") })
      .trim(),
    middle_name: z.string().trim().optional(),
    email: z.string().email({ message: t('Неправильний email') }),
    phone: z
      .string()
      .min(1, { message: t("Поле є обов'язковим") })
      .trim(),
    birthday: z
      .string()
      .min(1, { message: t("Поле є обов'язковим") })
      .trim(),
    custom_fields: customFieldsSchema.optional(),
    recaptcha_token: z.string().optional(),
    agreement: z.boolean().optional(),
  });
}

/**
 * Determines if a custom field is required based on its type and settings.
 * For select fields: required only if ALL options have non-empty values (no empty option exists)
 */
export function isCustomFieldRequired(field: CustomFieldWithValue): boolean {
  if (field.field_type !== 'select') {
    return false;
  }

  const allowedValues = field.settings?.allowed_values || [];
  // Required if all options have non-empty id (no empty option exists)
  return allowedValues.length > 0 && allowedValues.every((val) => val !== '');
}

/**
 * Creates validation config from custom fields array
 */
export function createCustomFieldsValidationConfig(
  customFields: CustomFieldWithValue[],
): CustomFieldValidationConfig[] {
  return customFields.map((field) => ({
    fieldId: field.id,
    isRequired: isCustomFieldRequired(field),
  }));
}

export type SignUpFormValues = z.infer<
  ReturnType<typeof createSignUpFormSchema>
>;
